import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, where, updateDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';
import UserNav from '../../components/navbar/user-nav';
import Breadcrumb from '../../components/Breadcrumb';

interface Propiedad {
  id: string; // doc.id de Firestore
  list_number?: string;
  propiedad_id?: string;
  titulo?: string;
  tipo_propiedad?: string;
  precio_actual_mxn?: number;
  precio_actual_usd?: number;
  inmobiliaria_doc_id?: string;
  asesor_doc_ids?: string[];
  asociado_doc_id?: string;
  vendedor_nombre?: string;
}

interface Asociado {
  id: string;
  nombre: string;
  nombreEnFlex?: string;
  correo: string;
  inmobiliaria_doc_id?: string;
}

const AsignarPropiedadesMiriam: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  const [loading, setLoading] = useState(false);
  const [miriam, setMiriam] = useState<Asociado | null>(null);
  const [propiedadesEncontradas, setPropiedadesEncontradas] = useState<Propiedad[]>([]);
  const [propiedadesAsignadas, setPropiedadesAsignadas] = useState<string[]>([]);
  const [logs, setLogs] = useState<string[]>([]);

  // IDs de Flex de las propiedades de Miriam
  const MIRIAM_PROPERTY_IDS = ['31804', '33166', '31188'];
  const MIRIAM_EMAIL = 'miriam@gugorealestate.com';
  const GUGO_INMOBILIARIA_ID = 'x5uxbDuJ9ydhecTyVliB';

  const addLog = (msg: string) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()} - ${msg}`]);
    if (process.env.NODE_ENV === "development") console.log(msg);
  };

  // Buscar a Miriam en Firestore
  const buscarMiriam = async () => {
    addLog('🔍 Buscando a Miriam González en Firestore...');

    const q = query(collection(db, 'asociado'), where('correo', '==', MIRIAM_EMAIL));
    const snap = await getDocs(q);

    if (snap.empty) {
      addLog('❌ Miriam no encontrada en la colección asociado');
      return null;
    }

    const docData = snap.docs[0];
    const data = docData.data();
    const miriamData: Asociado = {
      id: docData.id,
      nombre: data.nombre,
      nombreEnFlex: data.nombreEnFlex,
      correo: data.correo,
      inmobiliaria_doc_id: data.inmobiliaria_doc_id
    };

    addLog(`✅ Miriam encontrada: Doc ID = ${miriamData.id}`);
    addLog(`   Nombre: ${miriamData.nombre}`);
    addLog(`   Inmobiliaria Doc ID: ${miriamData.inmobiliaria_doc_id || 'NO ASIGNADA'}`);

    setMiriam(miriamData);
    return miriamData;
  };

  // Buscar propiedades por ID de Flex
  const buscarPropiedades = async () => {
    addLog('🔍 Buscando propiedades de Miriam...');

    const propiedadesSnap = await getDocs(collection(db, 'propiedades'));
    const encontradas: Propiedad[] = [];

    propiedadesSnap.docs.forEach(docSnap => {
      const data = docSnap.data();
      const listNumber = data.list_number || data.propiedad_id || data.id || '';

      // Buscar por list_number, propiedad_id, id, o vendedor_nombre
      const matchById = MIRIAM_PROPERTY_IDS.some(pid =>
        listNumber.toString().includes(pid) ||
        (data.id && data.id.toString().includes(pid))
      );

      const matchByVendedor = data.vendedor_nombre &&
        data.vendedor_nombre.toLowerCase().includes('miriam') &&
        data.vendedor_nombre.toLowerCase().includes('gonzalez');

      if (matchById || matchByVendedor) {
        encontradas.push({
          id: docSnap.id,
          list_number: data.list_number,
          propiedad_id: data.propiedad_id,
          titulo: data.titulo || data.nombre || 'Sin título',
          tipo_propiedad: data.tipo_propiedad || data.tipo,
          precio_actual_mxn: data.precio_actual_mxn,
          precio_actual_usd: data.precio_actual_usd,
          inmobiliaria_doc_id: data.inmobiliaria_doc_id,
          asesor_doc_ids: data.asesor_doc_ids,
          asociado_doc_id: data.asociado_doc_id,
          vendedor_nombre: data.vendedor_nombre
        });

        addLog(`   ✅ Propiedad encontrada: ${data.list_number || data.id} - ${data.titulo || 'Sin título'}`);
      }
    });

    if (encontradas.length === 0) {
      addLog('⚠️ No se encontraron propiedades con los IDs especificados');
      addLog('   Buscando por nombre de vendedor "Miriam Alejandra Gonzalez Gonzalez"...');
    } else {
      addLog(`📊 Total propiedades encontradas: ${encontradas.length}`);
    }

    setPropiedadesEncontradas(encontradas);
    return encontradas;
  };

  // Asignar propiedades a Miriam y su agencia
  const asignarPropiedades = async () => {
    if (!miriam) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Primero debes buscar a Miriam',
        confirmButtonColor: '#d33'
      });
      return;
    }

    if (propiedadesEncontradas.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin propiedades',
        text: 'No hay propiedades para asignar',
        confirmButtonColor: '#f0ad4e'
      });
      return;
    }

    const confirm = await Swal.fire({
      icon: 'question',
      title: '¿Asignar propiedades?',
      html: `
        <p>Se asignarán <strong>${propiedadesEncontradas.length}</strong> propiedades a:</p>
        <p><strong>Asociado:</strong> ${miriam.nombre} (${miriam.id})</p>
        <p><strong>Inmobiliaria:</strong> ${GUGO_INMOBILIARIA_ID}</p>
      `,
      showCancelButton: true,
      confirmButtonText: 'Sí, asignar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745'
    });

    if (!confirm.isConfirmed) return;

    setLoading(true);
    const asignadas: string[] = [];

    for (const propiedad of propiedadesEncontradas) {
      try {
        const updates: any = {
          // Asignar a Miriam como asociado
          asociado_doc_id: miriam.id,
          asesor_doc_ids: [miriam.id], // También en el array de asesores

          // Asignar a la inmobiliaria GUGO
          inmobiliaria_doc_id: GUGO_INMOBILIARIA_ID,

          // Metadata
          fecha_asignacion: new Date().toISOString(),
          asignado_por: 'admin-script'
        };

        await updateDoc(doc(db, 'propiedades', propiedad.id), updates);

        addLog(`✅ Propiedad ${propiedad.list_number || propiedad.id} asignada correctamente`);
        asignadas.push(propiedad.id);
      } catch (error: any) {
        addLog(`❌ Error asignando ${propiedad.list_number}: ${error.message}`);
      }
    }

    setPropiedadesAsignadas(asignadas);
    setLoading(false);

    Swal.fire({
      icon: 'success',
      title: '¡Propiedades asignadas!',
      html: `
        <p>Se asignaron <strong>${asignadas.length}</strong> de ${propiedadesEncontradas.length} propiedades</p>
        <p>A: <strong>${miriam.nombre}</strong></p>
        <p>Inmobiliaria: <strong>GUGO REAL ESTATE PV</strong></p>
      `,
      confirmButtonColor: '#28a745'
    });

    // Recargar propiedades para ver el estado actualizado
    await buscarPropiedades();
  };

  // Ejecutar búsqueda completa
  const ejecutarAnalisis = async () => {
    setLoading(true);
    setLogs([]);
    setPropiedadesEncontradas([]);
    setPropiedadesAsignadas([]);

    try {
      addLog('🚀 Iniciando análisis...');
      await buscarMiriam();
      await buscarPropiedades();
      addLog('✅ Análisis completado');
    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Verificar permisos
  if (!user?.tipo?.includes('admin')) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <h4>Acceso denegado</h4>
          <p>Solo administradores pueden acceder a esta página.</p>
          <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />
      <div className="container py-4">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Asignar Propiedades a Miriam' }
          ]}
        />

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-primary text-white">
            <h4 className="mb-0">
              <i className="fas fa-home me-2"></i>
              Asignar Propiedades a Miriam González
            </h4>
          </div>
          <div className="card-body">
            <div className="alert alert-info">
              <h5><i className="fas fa-info-circle me-2"></i>Información</h5>
              <p className="mb-1">Este script busca y asigna las siguientes propiedades a Miriam:</p>
              <ul className="mb-0">
                <li><strong>31804</strong> - Condo en Centro North, 5 de diciembre ($4,850,000 MXN)</li>
                <li><strong>33166</strong> - Terreno en Nuevo Vallarta West, Los Tigres ($8,600,000 MXN)</li>
                <li><strong>31188</strong> - Casa en Ixtapa, Banus ($3,300,000 MXN)</li>
              </ul>
            </div>

            {/* Datos conocidos */}
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card border-success h-100">
                  <div className="card-header bg-success text-white">
                    <i className="fas fa-user me-2"></i>Asociado
                  </div>
                  <div className="card-body">
                    <p><strong>Nombre:</strong> Miriam González</p>
                    <p><strong>Correo:</strong> {MIRIAM_EMAIL}</p>
                    <p><strong>Doc ID:</strong> {miriam?.id || 'Por buscar...'}</p>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-warning h-100">
                  <div className="card-header bg-warning text-dark">
                    <i className="fas fa-building me-2"></i>Inmobiliaria
                  </div>
                  <div className="card-body">
                    <p><strong>Nombre:</strong> GUGO REAL ESTATE PV</p>
                    <p><strong>Doc ID:</strong> <code>{GUGO_INMOBILIARIA_ID}</code></p>
                  </div>
                </div>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="d-flex gap-3 mb-4">
              <button
                className="btn btn-primary btn-lg"
                onClick={ejecutarAnalisis}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Analizando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2"></i>
                    1. Buscar Miriam y Propiedades
                  </>
                )}
              </button>

              <button
                className="btn btn-success btn-lg"
                onClick={asignarPropiedades}
                disabled={loading || !miriam || propiedadesEncontradas.length === 0}
              >
                <i className="fas fa-link me-2"></i>
                2. Asignar Propiedades
              </button>
            </div>

            {/* Propiedades encontradas */}
            {propiedadesEncontradas.length > 0 && (
              <div className="card border-info mb-4">
                <div className="card-header bg-info text-white">
                  <i className="fas fa-list me-2"></i>
                  Propiedades Encontradas: {propiedadesEncontradas.length}
                </div>
                <div className="card-body">
                  <table aria-label="Tabla de Asignar Propiedades Miriam" className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th scope="col">List Number</th>
                        <th scope="col">Título</th>
                        <th scope="col">Tipo</th>
                        <th scope="col">Precio MXN</th>
                        <th scope="col">Vendedor</th>
                        <th scope="col">Inmobiliaria Actual</th>
                        <th scope="col">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {propiedadesEncontradas.map((p, idx) => (
                        <tr key={idx} className={propiedadesAsignadas.includes(p.id) ? 'table-success' : ''}>
                          <td><code>{p.list_number || p.propiedad_id || '-'}</code></td>
                          <td>{p.titulo}</td>
                          <td>{p.tipo_propiedad}</td>
                          <td>${p.precio_actual_mxn?.toLocaleString() || '-'}</td>
                          <td>{p.vendedor_nombre || '-'}</td>
                          <td>
                            {p.inmobiliaria_doc_id === GUGO_INMOBILIARIA_ID ? (
                              <span className="badge bg-success">GUGO ✓</span>
                            ) : p.inmobiliaria_doc_id ? (
                              <code className="text-warning">{p.inmobiliaria_doc_id.substring(0, 8)}...</code>
                            ) : (
                              <span className="badge bg-danger">Sin asignar</span>
                            )}
                          </td>
                          <td>
                            {propiedadesAsignadas.includes(p.id) ? (
                              <span className="badge bg-success">
                                <i className="fas fa-check me-1"></i>Asignada
                              </span>
                            ) : (
                              <span className="badge bg-secondary">Pendiente</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Logs */}
            {logs.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <i className="fas fa-terminal me-2"></i>
                  Registro de operaciones
                </div>
                <div className="card-body bg-dark text-light" style={{ maxHeight: '300px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {logs.map((log, idx) => (
                    <div key={idx} className={
                      log.includes('❌') ? 'text-danger' :
                      log.includes('✅') ? 'text-success' :
                      log.includes('⚠️') ? 'text-warning' :
                      'text-info'
                    }>
                      {log}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AsignarPropiedadesMiriam;
